import { DateTime } from "luxon";
import { normalizeEducationLevel } from "@portal/app/_components/formFunnel/utils/normalizeEducationLevel";
import type { CustomerDataBuilder } from "./types";

/**
 * Processor for a specific customer field.
 * Handles formatting the raw value to a string and populating the ref.
 */
interface FieldProcessor {
  /**
   * Format the raw value to a string representation suitable for mutation.
   * @param value The raw value from the form
   * @returns The formatted string value
   */
  formatValue(value: unknown): string;

  /**
   * Populate the customerDataRef with the formatted value.
   * This is called BEFORE the mutation to ensure ConfirmStep has the data
   * even if the mutation fails.
   * @param ref The mutable ref to populate
   * @param stringValue The formatted string value
   */
  populateRef(ref: MutableRefObject<CustomerDataBuilder>, stringValue: string): void;
}

type MutableRefObject<T> = { current: T };

/**
 * Field processors for customer-mapped fields that ConfirmStep reads from
 * customerDataRef (currently `dob` and `educationLevel` — see ConfirmStep's
 * `hasEducationLevel` / `hasDob` checks). Fields NOT in this map are still
 * saved to the Customer record via the mutation but don't need ref population
 * because ConfirmStep doesn't gate on them. Add a processor here when
 * ConfirmStep starts reading a new field from the ref.
 */
const FIELD_PROCESSORS: Record<string, FieldProcessor> = {
  dob: {
    formatValue: (value: unknown): string => {
      if (DateTime.isDateTime(value)) {
        return value.toFormat("yyyy-MM-dd");
      }
      const parsed = DateTime.fromISO(String(value));
      return parsed.isValid ? parsed.toFormat("yyyy-MM-dd") : String(value);
    },
    populateRef: (ref, stringValue) => {
      ref.current.dob = stringValue;
    },
  },

  educationLevel: {
    formatValue: (value: unknown): string => String(value),
    populateRef: (ref, stringValue) => {
      const normalized = normalizeEducationLevel(stringValue);
      ref.current.educationLevel = normalized;
    },
  },
};

/**
 * Get the processor for a customer field.
 * @param customerField The field name (e.g., "dob", "educationLevel")
 * @returns The processor, or undefined if the field has no special handling
 */
export function getFieldProcessor(customerField: string): FieldProcessor | undefined {
  return FIELD_PROCESSORS[customerField];
}

/**
 * Format a value for a customer field.
 * If the field has a processor, uses the processor's formatting logic.
 * Otherwise, converts the value to a string.
 * @param value The raw value
 * @param customerField The field name
 * @returns The formatted string value
 */
export function formatCustomerFieldValue(value: unknown, customerField: string): string {
  const processor = getFieldProcessor(customerField);
  if (processor) {
    return processor.formatValue(value);
  }
  return String(value);
}

/**
 * Populate the customerDataRef with a formatted value.
 * If the field has a processor, uses the processor's ref population logic.
 * This is called BEFORE the mutation to ensure ConfirmStep has the data
 * even if the mutation fails.
 * @param ref The mutable ref to populate
 * @param customerField The field name
 * @param stringValue The formatted string value
 */
export function populateCustomerDataRef(
  ref: MutableRefObject<CustomerDataBuilder>,
  customerField: string,
  stringValue: string
): void {
  const processor = getFieldProcessor(customerField);
  if (processor) {
    processor.populateRef(ref, stringValue);
  }
}
